import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardHeader,
  Alert,
  CircularProgress,
  TextField,
  Stack,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Breadcrumbs,
  Link,
  Chip,
  FormControl,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Link as LinkIcon,
  Title as TitleIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import {
  NewsFeedCreateRequest,
  NewsFeedCreateSection,
  NewsFeedCreateContent,
  NewsFeedCreateListItem,
} from '../../types/newsfeed';
import { createNewsfeedDetail } from '../../endpoints/newsfeedTs';
import { hasPermission } from '../../utils/permissions';
import DateTimePicker from '../../Components/DateTimePicker';
import ChapterSelector from '../../Components/ChapterSelector';
import { useApiSend } from '../../utils/httpClientTs';
import dayjs from 'dayjs';

// Mock content type IDs for testing
const CONTENT_TYPES = {
  paragraph: '60308536096c4ce088bee75af7ae8ae2',
  list: 'c1e257bc8c674ae28a7b2085518aea9e',
  video: 'fd9dd86a20e24e79b920e191f3b8203a',
};

export default function NewsfeedCreate(): React.JSX.Element {
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<NewsFeedCreateRequest>({
    title: '',
    description: '',
    image_url: '',
    link_url: '',
    sections: [
      {
        title: 'Introduction',
        order: 1,
        content: [
          {
            content_type_id: CONTENT_TYPES.paragraph,
            title: null,
            list: null,
            paragraph: '',
            order: 1,
            video_url: null,
          },
        ],
      },
    ],
    start_date: dayjs().format('YYYY-MM-DD'),
    end_date: dayjs().add(1, 'year').format('YYYY-MM-DD'),
    is_active: true,
    local_chapter_id: null,
    spd_chapter_id: null,
    is_international: true,
  });

  // Check permissions
  const canCreate = hasPermission('NEWSFEED_CREATE');

  // API mutation hook using useApiSend
  const { mutate: createNewsfeed, isPending: isSaving } = useApiSend(
    createNewsfeedDetail,
    (data) => {
      setShowSuccess(true);
      setTimeout(() => {
        navigate('/newsfeed');
      }, 2000);
    },
    (error) => {
      setError(error.message || 'Failed to create newsfeed');
      console.error('Error creating newsfeed:', error);
    }
  );

  const handleInputChange = (
    field: keyof NewsFeedCreateRequest,
    value: string | null | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDateChange = (field: 'start_date' | 'end_date', value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value.format('YYYY-MM-DD') }));
  };

  const handleChapterSelect = (
    field: 'local_chapter_id' | 'spd_chapter_id',
    value: string | null
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSectionTitleChange = (sectionIndex: number, title: string) => {
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.map((section, index) =>
        index === sectionIndex ? { ...section, title } : section
      ),
    }));
  };

  const handleContentChange = (
    sectionIndex: number,
    contentIndex: number,
    field: string,
    value: any
  ) => {
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.map((section, sIndex) =>
        sIndex === sectionIndex
          ? {
              ...section,
              content: section.content.map((content, cIndex) =>
                cIndex === contentIndex
                  ? { ...content, [field]: value }
                  : content
              ),
            }
          : section
      ),
    }));
  };

  const addSection = () => {
    const newSection: NewsFeedCreateSection = {
      title: `Section ${formData.sections.length + 1}`,
      order: formData.sections.length + 1,
      content: [
        {
          content_type_id: CONTENT_TYPES.paragraph,
          title: null,
          list: null,
          paragraph: '',
          order: 1,
          video_url: null,
        },
      ],
    };

    setFormData((prev) => ({
      ...prev,
      sections: [...prev.sections, newSection],
    }));
  };

  const removeSection = (sectionIndex: number) => {
    if (formData.sections.length > 1) {
      setFormData((prev) => ({
        ...prev,
        sections: prev.sections.filter((_, index) => index !== sectionIndex),
      }));
    }
  };

  const addContent = (
    sectionIndex: number,
    contentType: 'paragraph' | 'list' | 'video'
  ) => {
    const section = formData.sections[sectionIndex];
    const newContent: NewsFeedCreateContent = {
      content_type_id: CONTENT_TYPES[contentType],
      title: contentType === 'list' ? 'New List' : null,
      list:
        contentType === 'list'
          ? [{ content: '', order: 1, is_link: false, link_url: null }]
          : null,
      paragraph: contentType === 'paragraph' ? '' : null,
      order: section.content.length + 1,
      video_url: contentType === 'video' ? '' : null,
    };

    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.map((section, index) =>
        index === sectionIndex
          ? { ...section, content: [...section.content, newContent] }
          : section
      ),
    }));
  };

  const removeContent = (sectionIndex: number, contentIndex: number) => {
    const section = formData.sections[sectionIndex];
    if (section.content.length > 1) {
      setFormData((prev) => ({
        ...prev,
        sections: prev.sections.map((section, sIndex) =>
          sIndex === sectionIndex
            ? {
                ...section,
                content: section.content.filter(
                  (_, cIndex) => cIndex !== contentIndex
                ),
              }
            : section
        ),
      }));
    }
  };

  const addListItem = (sectionIndex: number, contentIndex: number) => {
    const content = formData.sections[sectionIndex].content[contentIndex];
    if (content.list) {
      const newItem: NewsFeedCreateListItem = {
        content: '',
        order: content.list.length + 1,
        is_link: false,
        link_url: null,
      };

      setFormData((prev) => ({
        ...prev,
        sections: prev.sections.map((section, sIndex) =>
          sIndex === sectionIndex
            ? {
                ...section,
                content: section.content.map((content, cIndex) =>
                  cIndex === contentIndex
                    ? { ...content, list: [...content.list!, newItem] }
                    : content
                ),
              }
            : section
        ),
      }));
    }
  };

  const removeListItem = (
    sectionIndex: number,
    contentIndex: number,
    itemIndex: number
  ) => {
    const content = formData.sections[sectionIndex].content[contentIndex];
    if (content.list && content.list.length > 1) {
      setFormData((prev) => ({
        ...prev,
        sections: prev.sections.map((section, sIndex) =>
          sIndex === sectionIndex
            ? {
                ...section,
                content: section.content.map((content, cIndex) =>
                  cIndex === contentIndex
                    ? {
                        ...content,
                        list:
                          content.list?.filter(
                            (_, iIndex) => iIndex !== itemIndex
                          ) || [],
                      }
                    : content
                ),
              }
            : section
        ),
      }));
    }
  };

  const handleListItemChange = (
    sectionIndex: number,
    contentIndex: number,
    itemIndex: number,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.map((section, sIndex) =>
        sIndex === sectionIndex
          ? {
              ...section,
              content: section.content.map((content, cIndex) =>
                cIndex === contentIndex
                  ? {
                      ...content,
                      list:
                        content.list?.map((item, iIndex) =>
                          iIndex === itemIndex
                            ? { ...item, content: value }
                            : item
                        ) || [],
                    }
                  : content
              ),
            }
          : section
      ),
    }));
  };

  const handleListItemLinkChange = (
    sectionIndex: number,
    contentIndex: number,
    itemIndex: number,
    field: 'is_link' | 'link_url',
    value: boolean | string
  ) => {
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.map((section, sIndex) =>
        sIndex === sectionIndex
          ? {
              ...section,
              content: section.content.map((content, cIndex) =>
                cIndex === contentIndex
                  ? {
                      ...content,
                      list:
                        content.list?.map((item, iIndex) =>
                          iIndex === itemIndex
                            ? { ...item, [field]: value }
                            : item
                        ) || [],
                    }
                  : content
              ),
            }
          : section
      ),
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.title.trim()) return false;
    if (!formData.description?.trim()) return false;
    if (!formData.start_date) return false;
    if (!formData.end_date) return false;

    // Validate that end date is after start date
    if (dayjs(formData.end_date).isBefore(dayjs(formData.start_date))) {
      return false;
    }

    // Check if all sections have content
    for (const section of formData.sections) {
      if (!section.title?.trim()) return false;
      for (const content of section.content) {
        if (
          content.content_type_id === CONTENT_TYPES.paragraph &&
          !content.paragraph?.trim()
        )
          return false;
        if (
          content.content_type_id === CONTENT_TYPES.list &&
          (!content.title?.trim() ||
            !content.list?.some((item) => item.content.trim()))
        )
          return false;
      }
    }

    return true;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    setError(null);

    // Use the API mutation hook
    createNewsfeed(formData);
  };

  const handleDiscard = () => {
    setShowDiscardDialog(true);
  };

  const confirmDiscard = () => {
    setShowDiscardDialog(false);
    navigate('/newsfeed');
  };

  if (!canCreate) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You don't have permission to create newsfeeds. Please contact an
          administrator.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/newsfeed')}
          sx={{ cursor: 'pointer' }}
        >
          Newsfeeds
        </Link>
        <Typography color="text.primary">Create Newsfeed</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Card elevation={3} sx={{ mb: 4 }}>
        <CardHeader
          title={
            <Typography variant="h4" component="h1" color="primary">
              Create New Newsfeed
            </Typography>
          }
          subheader={
            <Typography variant="body1" color="text.secondary">
              Build a comprehensive newsfeed with multiple sections and rich
              content
            </Typography>
          }
          action={
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/newsfeed')}
                size="large"
              >
                Back to List
              </Button>
            </Stack>
          }
        />
      </Card>

      {/* Success Alert */}
      {showSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Newsfeed created successfully! Redirecting to newsfeed list...
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Main Form */}
      <Grid container spacing={3}>
        {/* Left Column - Main Content */}
        <Grid item xs={12} lg={8}>
          {/* Basic Information */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <TitleIcon color="primary" />
              Basic Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter newsfeed title"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  value={formData.description || ''}
                  onChange={(e) =>
                    handleInputChange('description', e.target.value)
                  }
                  placeholder="Enter newsfeed description"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Image URL"
                  value={formData.image_url || ''}
                  onChange={(e) =>
                    handleInputChange('image_url', e.target.value)
                  }
                  placeholder="https://example.com/image.jpg"
                  InputProps={{
                    startAdornment: (
                      <ImageIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Link URL"
                  value={formData.link_url || ''}
                  onChange={(e) =>
                    handleInputChange('link_url', e.target.value)
                  }
                  placeholder="https://example.com"
                  InputProps={{
                    startAdornment: (
                      <LinkIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Newsfeed Settings */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Newsfeed Settings
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  date={dayjs(formData.start_date)}
                  label="Start Date"
                  minDate={null}
                  maxDate={null}
                  handleDateChange={(value) =>
                    handleDateChange('start_date', value)
                  }
                  extraError={false}
                  extraErrorMessage=""
                  handleErrorUpdate={() => {}}
                  disableFuture={false}
                  required={true}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  date={dayjs(formData.end_date)}
                  label="End Date"
                  minDate={null}
                  maxDate={null}
                  handleDateChange={(value) =>
                    handleDateChange('end_date', value)
                  }
                  extraError={false}
                  extraErrorMessage=""
                  handleErrorUpdate={() => {}}
                  disableFuture={false}
                  required={true}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={(e) =>
                          handleInputChange('is_active', e.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label="Active"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_international}
                        onChange={(e) =>
                          handleInputChange(
                            'is_international',
                            e.target.checked
                          )
                        }
                        color="primary"
                      />
                    }
                    label="International"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <ChapterSelector
                  chapter={formData.local_chapter_id}
                  handleChapterSelect={(value) =>
                    handleChapterSelect('local_chapter_id', value)
                  }
                  spd={false}
                  reportId="newsfeed-create"
                  label="Local Chapter"
                  all={true}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ChapterSelector
                  chapter={formData.spd_chapter_id}
                  handleChapterSelect={(value) =>
                    handleChapterSelect('spd_chapter_id', value)
                  }
                  spd={true}
                  reportId="newsfeed-create"
                  label="SPD Chapter"
                  all={true}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Content Sections */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <DescriptionIcon color="primary" />
                Content Sections
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addSection}
                size="small"
              >
                Add Section
              </Button>
            </Box>

            {formData.sections.map((section, sectionIndex) => (
              <Box
                key={sectionIndex}
                sx={{
                  mb: 4,
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <TextField
                    fullWidth
                    label="Section Title"
                    value={section.title || ''}
                    onChange={(e) =>
                      handleSectionTitleChange(sectionIndex, e.target.value)
                    }
                    placeholder="Enter section title"
                    required
                    sx={{ mr: 2 }}
                  />
                  {formData.sections.length > 1 && (
                    <IconButton
                      color="error"
                      onClick={() => removeSection(sectionIndex)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Section Content */}
                {section.content.map((content, contentIndex) => (
                  <Box
                    key={contentIndex}
                    sx={{ mb: 3, pl: 2, borderLeft: '2px solid #e0e0e0' }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                      }}
                    >
                      <Chip
                        label={
                          content.content_type_id === CONTENT_TYPES.paragraph
                            ? 'Paragraph'
                            : content.content_type_id === CONTENT_TYPES.list
                              ? 'List'
                              : 'Video'
                        }
                        color={
                          content.content_type_id === CONTENT_TYPES.paragraph
                            ? 'primary'
                            : content.content_type_id === CONTENT_TYPES.list
                              ? 'secondary'
                              : 'info'
                        }
                        size="small"
                      />

                      {section.content.length > 1 && (
                        <IconButton
                          color="error"
                          onClick={() =>
                            removeContent(sectionIndex, contentIndex)
                          }
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>

                    {content.content_type_id === CONTENT_TYPES.paragraph ? (
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Paragraph Content"
                        value={content.paragraph || ''}
                        onChange={(e) =>
                          handleContentChange(
                            sectionIndex,
                            contentIndex,
                            'paragraph',
                            e.target.value
                          )
                        }
                        placeholder="Enter paragraph content"
                        required
                      />
                    ) : content.content_type_id === CONTENT_TYPES.list ? (
                      <Box>
                        <TextField
                          fullWidth
                          label="List Title"
                          value={content.title || ''}
                          onChange={(e) =>
                            handleContentChange(
                              sectionIndex,
                              contentIndex,
                              'title',
                              e.target.value
                            )
                          }
                          placeholder="Enter list title"
                          required
                          sx={{ mb: 2 }}
                        />
                        {content.list?.map((item, itemIndex) => (
                          <Box
                            key={itemIndex}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <TextField
                              fullWidth
                              label={`List Item ${itemIndex + 1}`}
                              value={item.content}
                              onChange={(e) =>
                                handleListItemChange(
                                  sectionIndex,
                                  contentIndex,
                                  itemIndex,
                                  e.target.value
                                )
                              }
                              placeholder="Enter list item content"
                              required
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={item.is_link}
                                  onChange={(e) =>
                                    handleListItemLinkChange(
                                      sectionIndex,
                                      contentIndex,
                                      itemIndex,
                                      'is_link',
                                      e.target.checked
                                    )
                                  }
                                  color="primary"
                                />
                              }
                              label="Link"
                            />
                            {item.is_link && (
                              <TextField
                                fullWidth
                                label="Link URL"
                                value={item.link_url || ''}
                                onChange={(e) =>
                                  handleListItemLinkChange(
                                    sectionIndex,
                                    contentIndex,
                                    itemIndex,
                                    'link_url',
                                    e.target.value
                                  )
                                }
                                placeholder="https://example.com"
                                InputProps={{
                                  startAdornment: (
                                    <LinkIcon
                                      sx={{ mr: 1, color: 'text.secondary' }}
                                    />
                                  ),
                                }}
                              />
                            )}
                            {content.list && content.list.length > 1 && (
                              <IconButton
                                color="error"
                                onClick={() =>
                                  removeListItem(
                                    sectionIndex,
                                    contentIndex,
                                    itemIndex
                                  )
                                }
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Box>
                        ))}
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() =>
                            addListItem(sectionIndex, contentIndex)
                          }
                          size="small"
                          sx={{ mt: 1 }}
                        >
                          Add List Item
                        </Button>
                      </Box>
                    ) : content.content_type_id === CONTENT_TYPES.video ? (
                      <Box>
                        <TextField
                          fullWidth
                          label="Video URL"
                          value={content.video_url || ''}
                          onChange={(e) =>
                            handleContentChange(
                              sectionIndex,
                              contentIndex,
                              'video_url',
                              e.target.value
                            )
                          }
                          placeholder="https://www.youtube.com/watch?v=..."
                          InputProps={{
                            startAdornment: (
                              <LinkIcon
                                sx={{ mr: 1, color: 'text.secondary' }}
                              />
                            ),
                          }}
                        />
                      </Box>
                    ) : null}
                  </Box>
                ))}

                {/* Add Content Buttons */}
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addContent(sectionIndex, 'paragraph')}
                    size="small"
                  >
                    Add Paragraph
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addContent(sectionIndex, 'list')}
                    size="small"
                  >
                    Add List
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addContent(sectionIndex, 'video')}
                    size="small"
                  >
                    Add Video
                  </Button>
                </Box>
              </Box>
            ))}
          </Paper>
        </Grid>

        {/* Right Column - Actions & Preview */}
        <Grid item xs={12} lg={4}>
          {/* Form Actions */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Actions
            </Typography>
            <Stack spacing={2}>
              <Button
                variant="contained"
                startIcon={
                  isSaving ? <CircularProgress size={20} /> : <SaveIcon />
                }
                onClick={handleSave}
                disabled={isSaving || !validateForm()}
                fullWidth
                size="large"
              >
                {isSaving ? 'Creating...' : 'Create Newsfeed'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleDiscard}
                disabled={isSaving}
                fullWidth
                size="large"
              >
                Discard Changes
              </Button>
            </Stack>
          </Paper>

          {/* Preview */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Preview
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Title
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {formData.title || 'No title entered'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Description
                </Typography>
                <Typography variant="body1">
                  {formData.description || 'No description entered'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Sections
                </Typography>
                <Typography variant="body1">
                  {formData.sections.length} section
                  {formData.sections.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Start Date
                </Typography>
                <Typography variant="body1">
                  {formData.start_date
                    ? new Date(formData.start_date).toLocaleDateString()
                    : 'Not set'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  End Date
                </Typography>
                <Typography variant="body1">
                  {formData.end_date
                    ? new Date(formData.end_date).toLocaleDateString()
                    : 'Not set'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Status
                </Typography>
                <Chip
                  label={formData.is_active ? 'Active' : 'Inactive'}
                  color={formData.is_active ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Scope
                </Typography>
                <Chip
                  label={
                    formData.is_international
                      ? 'International'
                      : 'Chapter Specific'
                  }
                  color={formData.is_international ? 'primary' : 'secondary'}
                  size="small"
                />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Date Range
                </Typography>
                <Typography variant="body1">
                  {formData.start_date} to {formData.end_date}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Status
                </Typography>
                <Chip
                  label={formData.is_active ? 'Active' : 'Inactive'}
                  color={formData.is_active ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Scope
                </Typography>
                <Typography variant="body1">
                  {formData.is_international ? 'International' : 'Local/SPD'}
                </Typography>
              </Box>
              {!formData.is_international &&
                (formData.local_chapter_id || formData.spd_chapter_id) && (
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Chapters
                    </Typography>
                    <Typography variant="body1">
                      {formData.local_chapter_id && 'Local Chapter Selected'}
                      {formData.local_chapter_id &&
                        formData.spd_chapter_id &&
                        ' & '}
                      {formData.spd_chapter_id && 'SPD Chapter Selected'}
                    </Typography>
                  </Box>
                )}
              {formData.image_url && (
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Preview Image
                  </Typography>
                  <img
                    src={formData.image_url}
                    alt="Newsfeed preview"
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0',
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </Box>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Discard Confirmation Dialog */}
      <Dialog
        open={showDiscardDialog}
        onClose={() => setShowDiscardDialog(false)}
      >
        <DialogTitle>Discard Changes?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to discard all changes? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDiscardDialog(false)}>Cancel</Button>
          <Button onClick={confirmDiscard} color="error" variant="contained">
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
